import React, { useState } from "react";
import ToolTip from "../tooltip/Tooltip";

interface CopyableTextProps {
  shortText: string;
  longText: string;
  maxWidth?: string;
  className?: string;
  style?: React.CSSProperties;
  tooltipText?: string;
}

const CopyableText = ({
  shortText,
  longText,
  maxWidth = "300px",
  className = "",
  style,
  tooltipText,
}: CopyableTextProps) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(longText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  // The content of the component that will be conditionally wrapped with a tooltip
  const content = (
    <div
      onClick={copyToClipboard}
      className={`inline-flex items-center px-3 py-1 bg-gray-100 rounded cursor-pointer hover:bg-gray-200 transition-colors relative ${className}`}
      style={style}
    >
      <div
        className={`flex items-center transition-opacity duration-300 ${
          copied ? "opacity-0" : "opacity-100"
        }`}
      >
        <span className="mr-2" style={{ maxWidth, textOverflow: "ellipsis", overflow: "hidden" }}>
          {shortText}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M3.33333 10H2.66667C2.31305 10 1.97391 9.85953 1.72386 9.60949C1.47381 9.35944 1.33333 9.0203 1.33333 8.66667V2.66667C1.33333 2.31305 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31305 1.33333 2.66667 1.33333H8.66667C9.0203 1.33333 9.35944 1.47381 9.60949 1.72386C9.85953 1.97391 10 2.31305 10 2.66667V3.33333"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* "Copied!" overlay */}
      <div
        className={`absolute inset-0 flex items-center justify-center bg-gray-100 rounded text-gray-800 transition-opacity duration-300 ${
          copied ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <span className="text-sm font-medium">Copied!</span>
      </div>
    </div>
  );

  // Only wrap with ToolTip if we have distinct text or an explicit tooltip
  return longText !== shortText || tooltipText ? (
    <ToolTip text={tooltipText ?? longText} maxWidth={maxWidth}>
      {content}
    </ToolTip>
  ) : (
    content
  );
};

export default CopyableText;
